import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import { FileText, ListChecks, Stack, TreeStructure } from 'phosphor-react';

import { ArvoreExemplo, DiagramaPartidaDobrada } from './diagramas';
import {
  Callout,
  ExemploTrabalhado,
  Forte,
  MiniKpiRow,
  Paragrafo,
  SecaoEnsino,
  SubTitulo,
  Termo,
  TocLayout,
} from './ui';

const SECOES = [
  { id: 'poc', label: 'O que é o Plano de Contas' },
  { id: 'grupos', label: 'Os grupos-chave' },
  { id: 'partida', label: 'Partida dobrada' },
  { id: 'transitoria', label: 'Contas que não podem sobrar' },
  { id: 'glossario', label: 'Glossário' },
];

export function AbaEntender() {
  return (
    <TocLayout secoes={SECOES}>
      <SecaoEnsino id="poc" numero={1} titulo="O que é o Plano de Contas">
        <Paragrafo>
          O <Forte>Plano de Contas</Forte> (POC) é a lista estruturada de todas as contas contábeis
          de uma empresa — é onde cada centavo que entra ou sai vai ser registrado. Conciliar um
          extrato bancário é, no fim, dizer <Forte>em qual conta do plano</Forte> cada lançamento
          entra.
        </Paragrafo>

        <SubTitulo>Sintética vs. analítica</SubTitulo>
        <Paragrafo>
          Toda conta é de um de dois tipos. A{' '}
          <Termo def="Conta-mãe, que só agrupa outras contas. Não recebe lançamento.">
            conta sintética
          </Termo>{' '}
          é agrupadora (ex.: “ATIVO”, “Disponível”) e <Forte>nunca recebe lançamento</Forte>. A{' '}
          <Termo def="Conta-folha, no fim da árvore. É a única que recebe lançamento.">
            conta analítica
          </Termo>{' '}
          é a folha — é nela que o lançamento de fato cai.
        </Paragrafo>
        <Callout tone="info" titulo="Regra de ouro">
          Uma sugestão de contrapartida <Forte>nunca</Forte> pode apontar para uma conta sintética —
          só para uma analítica. O ContaCil respeita isso automaticamente.
        </Callout>

        <SubTitulo>A “classificação” é o endereço da conta</SubTitulo>
        <Paragrafo>
          Cada conta tem uma <Forte>classificação</Forte> — um código hierárquico em que cada nível
          é concatenado ao anterior (como vem do Domínio, sem pontos):{' '}
          <Text as="span" fontFamily="mono" fontSize="13px">
            1 → 11 → 111 → 11101 → 111010001
          </Text>
          . O filho sempre começa com o código do pai, então a classificação descreve a árvore
          inteira.
        </Paragrafo>
        <Paragrafo>
          O ContaCil “pensa” sempre na <Forte>classificação</Forte> (não no código curto da conta),
          porque ela vem de um{' '}
          <Forte>template do Domínio praticamente idêntico entre todas as empresas</Forte> — o mesmo
          grupo tem a mesma classificação em centenas de empresas. Já o{' '}
          <Termo def="Número curto que vai no lançamento (ex.: 902). É sequencial e varia de empresa para empresa.">
            código
          </Termo>{' '}
          é arbitrário por empresa. Por isso o sistema raciocina pela classificação e só no fim
          traduz para o código daquela empresa.
        </Paragrafo>
        <Callout tone="aviso" titulo="O nome do banco engana">
          Empresas renomeiam a conta de banco com agência/número. Por isso o ContaCil casa a conta
          pelos <Forte>dígitos</Forte> da agência/conta, não pelo nome.
        </Callout>

        <ArvoreExemplo />
      </SecaoEnsino>

      <SecaoEnsino id="grupos" numero={2} titulo="Os grupos-chave para a conciliação bancária">
        <Paragrafo>
          Nem todo o plano interessa à conciliação. Estes são os grupos que o ContaCil mais usa (e
          que você vê na aba <Forte>Plano da empresa</Forte>):
        </Paragrafo>
        <Box
          as="ul"
          pl={5}
          maxW="68ch"
          color="#334155"
          fontSize="15px"
          lineHeight="1.7"
          sx={{ '& li': { mb: 2 } }}
        >
          <li>
            <Forte>Bancos / Disponível</Forte> — conta corrente (grupo <code>11102</code>), Caixa (
            <code>11101</code>) e aplicações (<code>11103</code> / <code>12102</code>). É a “perna
            banco”.
          </li>
          <li>
            <Forte>Sócios</Forte> (grupo <code>116</code>) — distribuição de lucros, empréstimos a
            pessoas ligadas.
          </li>
          <li>
            <Forte>Resultado financeiro</Forte> (grupo <code>581</code>) — tarifas, IOF, juros, IR
            sobre aplicação, rendimentos. É o grupo mais padronizado de todos.
          </li>
          <li>
            <Forte>Impostos e obrigações</Forte> (grupo <code>211</code>) — Simples, INSS, ICMS,
            ISS, PIS, COFINS, salários, parcelamentos.
          </li>
          <li>
            <Forte>Receitas</Forte> (<code>3xx</code>) e <Forte>Custos/Despesas</Forte> (
            <code>4xx</code>/<code>5xx</code>) — o resultado operacional.
          </li>
          <li>
            <Forte>Transitórias</Forte> (“a classificar”, “diversos”) — contas de passagem que{' '}
            <Forte>não podem sobrar</Forte> no fechamento (ver abaixo).
          </li>
        </Box>
        <Callout tone="info" titulo="Grupo é estável, folha varia">
          O <Forte>grupo</Forte> (prefixo da classificação) é o mesmo em quase toda empresa; a folha
          exata pode mudar de uma para outra. O ContaCil resolve a folha certa dentro de cada
          empresa.
        </Callout>
      </SecaoEnsino>

      <SecaoEnsino id="partida" numero={3} titulo="Partida dobrada: as duas pernas">
        <Paragrafo>
          Todo lançamento tem <Forte>duas pernas</Forte> de igual valor: um débito e um crédito. Na
          conciliação, uma perna é sempre o <Forte>banco</Forte>; a outra é a{' '}
          <Termo def="A conta do outro lado do banco no lançamento — a receita, despesa, sócio, etc.">
            contrapartida
          </Termo>
          .
        </Paragrafo>
        <Box
          as="ul"
          pl={5}
          maxW="68ch"
          color="#334155"
          fontSize="15px"
          lineHeight="1.7"
          sx={{ '& li': { mb: 2 } }}
        >
          <li>
            <Forte>Entrada de dinheiro</Forte> (crédito no extrato) → debita o banco, credita a
            contrapartida (uma receita).
          </li>
          <li>
            <Forte>Saída de dinheiro</Forte> (débito no extrato) → credita o banco, debita a
            contrapartida (uma despesa/ativo).
          </li>
        </Box>
        <Paragrafo>
          A perna do banco o ContaCil resolve <Forte>quase sempre sozinho</Forte> (~100%): procura a
          conta corrente da empresa e casa pelos dígitos da agência/conta. O desafio — e o assunto
          da aba <Forte>Como o ContaCil decide</Forte> — é a <Forte>contrapartida</Forte>.
        </Paragrafo>
        <DiagramaPartidaDobrada />
        <ExemploTrabalhado titulo="o lado-banco se resolve sozinho">
          Na EDITORA ROSSI, 100% da perna-banco caiu no Bradesco (conta corrente cadastrada) sem
          nenhuma adivinhação — exatamente porque o casamento por dígitos é determinístico.
        </ExemploTrabalhado>
      </SecaoEnsino>

      <SecaoEnsino id="transitoria" numero={4} titulo="Contas que não podem sobrar">
        <Paragrafo>
          Contas <Forte>transitórias</Forte> ou provisórias (“a classificar”, “compensar”,
          “diversos”) são caixas de passagem: existem no plano, mas{' '}
          <Forte>não podem ficar com saldo no fechamento</Forte> da competência.
        </Paragrafo>
        <Paragrafo>
          O ContaCil <Forte>nunca trata uma conta provisória como verdade</Forte> e ativamente tenta
          resolver o que está parado nela — por exemplo, um recebimento em “a classificar” vira uma
          busca pelo cliente que pagou, para mandar à conta de receita certa. O objetivo é{' '}
          <Forte>zerar</Forte> essas contas.
        </Paragrafo>
        <Callout tone="perigo" titulo="Atalho perigoso">
          Jogar tudo numa conta genérica (ex.: “Fornecedores Nacionais” para qualquer pagamento)
          parece resolver, mas esconde a natureza real do gasto. O validador sinaliza isso.
        </Callout>
      </SecaoEnsino>

      <SecaoEnsino id="glossario" numero={5} titulo="Glossário rápido">
        <MiniKpiRow
          kpis={[
            {
              label: 'Sintética',
              valor: 'agrupa',
              icon: <Stack size={18} weight="duotone" />,
              accent: '#6366F1',
              sub: 'não recebe lançamento',
            },
            {
              label: 'Analítica',
              valor: 'recebe',
              icon: <FileText size={18} weight="duotone" />,
              accent: '#10B981',
              sub: 'a folha do plano',
            },
            {
              label: 'Classificação',
              valor: 'endereço',
              icon: <TreeStructure size={18} weight="duotone" />,
              accent: '#1B88F5',
              sub: 'a âncora hierárquica',
            },
            {
              label: 'Contrapartida',
              valor: '2ª perna',
              icon: <ListChecks size={18} weight="duotone" />,
              accent: '#EC4899',
              sub: 'o outro lado do banco',
            },
          ]}
        />
        <Box as="dl" maxW="68ch" fontSize="14px" color="#334155" lineHeight="1.7">
          {[
            ['POC / Plano de Contas', 'lista estruturada de todas as contas contábeis da empresa.'],
            ['Conta sintética', 'conta-mãe agrupadora; não recebe lançamento.'],
            ['Conta analítica', 'conta-folha; é onde o lançamento cai.'],
            [
              'Classificação',
              'código hierárquico (ex.: 11101…) que dá o “endereço” da conta; a âncora do sistema.',
            ],
            [
              'Código',
              'número curto da conta naquela empresa; é o que sai no lançamento (varia por empresa).',
            ],
            ['Partida dobrada', 'todo lançamento tem débito e crédito de igual valor.'],
            ['Contrapartida', 'a conta do outro lado do banco no lançamento.'],
            [
              'Razão',
              'o histórico de lançamentos já feitos no Domínio; serve de “professor” para o sistema.',
            ],
            ['Competência', 'o mês/ano a que o fato contábil se refere.'],
            [
              'Conta transitória',
              'conta de passagem (“a classificar”) que deve ser zerada no fechamento.',
            ],
          ].map(([termo, def]) => (
            <Box key={termo} mb={2}>
              <Text as="dt" fontWeight="700" color="#0F172A" display="inline">
                {termo}:{' '}
              </Text>
              <Text as="dd" display="inline">
                {def}
              </Text>
            </Box>
          ))}
        </Box>
      </SecaoEnsino>
    </TocLayout>
  );
}
